import { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { Button, CurrencySelect, Icon } from "../../components/common";
import { ArchwayDetails } from "../../components/forms/invoice/ArchwayDetails";
import { NewBankAccountModal, NewWalletModal } from "../../components/modals";
import { WalletDropdown } from "../../components/wallet";
import { CURRENCY_MAP, DEFAULT_FIAT_CURRENCY } from "../../constants";
import DashboardLayout from "../../layouts/Dashboard.layout";
import { selectOnramps } from "../../store/reducers/onramps";
import { selectCurrentUser } from "../../store/reducers/user";
import { selectWallets } from "../../store/reducers/wallets";
import {
  useGetOnrampsQuery,
  useUpdateOnrampMutation,
} from "../../store/services/onramps";
import { useGetWalletsQuery } from "../../store/services/wallets";
import { Currency, CurrencyType } from "../../types/InvoiceTypes";
import { Wallet } from "../../types/WalletTypes";
import { formatAmount } from "../../utils/numbers/money";
import { toastSuccess } from "../../utils/toasts";
import { useAllowedChains } from "../../hooks/useAllowedChains";
import { ROUTES } from "../../routes";
import { useNavigate } from "react-router-dom";

export const DepositPage: FC = () => {
  const [selectedFiatCurrency, setSelectedFiatCurrency] = useState<Currency>(
    DEFAULT_FIAT_CURRENCY
  );
  const { allowed_incoming_currencies, allowed_outgoing_currencies } =
    useSelector(selectCurrentUser);

  const [selectedOutCurrency, setSelectedOutCurrency] = useState<Currency>(
    () => allowed_outgoing_currencies[0]
  );
  const [outCurrencyChanged, setOutCurrencyChanged] = useState(false);

  const [selectedWallet, setSelectedWallet] = useState<Wallet>();
  useGetOnrampsQuery();
  const onramps = useSelector(selectOnramps);
  useGetWalletsQuery();
  const walletList = useSelector(selectWallets);
  const [updateOnramp] = useUpdateOnrampMutation();
  const [showWalletForm, setShowWalletForm] = useState(false);
  const [showBankAccountForm, setShowBankAccountForm] = useState(false);

  const allowedChains = useAllowedChains(selectedOutCurrency);
  const allowedWallets = useMemo(
    () => walletList.filter(({ chain }) => allowedChains.includes(chain!)),
    [walletList, allowedChains]
  );

  useEffect(() => {
    if (onramps) {
      const foundWallet = allowedWallets?.find(
        ({ id }) => id === onramps.wallet_id
      );

      if (foundWallet) {
        setSelectedWallet(foundWallet);
      }
      if (!outCurrencyChanged && (onramps.outgoing_currency as Currency)) {
        setSelectedOutCurrency(onramps.outgoing_currency as Currency);
      }
    }
  }, [onramps, allowedWallets, selectedOutCurrency, outCurrencyChanged]);

  const selectedPaymentDetails = onramps?.bank_details?.find(
    (detail) => detail.currency === selectedFiatCurrency
  );

  const onSelectWallet = (wallet: Wallet) => {
    updateOnramp({
      wallet_id: wallet.id!,
      outgoing_currency: selectedOutCurrency,
    });
  };

  const CurrencySelector = (
    <CurrencySelect
      name="incoming_currency"
      allowedCurrencies={allowed_incoming_currencies}
      value={selectedFiatCurrency}
      currencyType={CurrencyType.FIAT}
      onChange={(e: ChangeEvent<HTMLInputElement>) => {
        setSelectedFiatCurrency(e.target.value as Currency);
      }}
    />
  );

  const paymentDetailsMessage = useMemo(() => {
    const sepaText =
      selectedFiatCurrency === Currency.EUR ? (
        <>
          {" "}
          via <span className="font-extrabold">SEPA/SEPA-Instant</span>
        </>
      ) : selectedFiatCurrency === Currency.USD ? (
        <>
          {" "}
          via <span className="font-extrabold">ACH/Wire</span>
        </>
      ) : undefined;

    return (
      <>
        Only send{" "}
        <span className="font-extrabold uppercase">{selectedFiatCurrency}</span>
        {sepaText} to these bank details.{" "}
        <span className="font-extrabold">IMPORTANT: NO SWIFT Payments!</span>
      </>
    );
  }, [selectedFiatCurrency]);

  const monthlyLimit = parseFloat(onramps?.eur_monthly_limit || "0");
  const monthTotal = parseFloat(onramps?.month_eur_total || "0");
  const monthlyLeft = monthlyLimit - monthTotal;

  const onCreateWallet = () => {
    if (CURRENCY_MAP[selectedOutCurrency].type === CurrencyType.CRYPTO) {
      setShowWalletForm(true);
      setShowBankAccountForm(false);
    } else {
      setShowWalletForm(false);
      setShowBankAccountForm(true);
    }
  };

  const depositText = useMemo(() => {
    if (selectedFiatCurrency === Currency.USD) {
      return (
        <>
          Deposit <b>USD</b> from <b>your own</b> bank account, or from{" "}
          <b>your</b> account on platforms such as{" "}
          <b>Upwork, Fiverr, Deel, PayPal</b>, etc.
        </>
      );
    }
    return (
      <>
        Deposit <b>{selectedFiatCurrency.toUpperCase()}</b> from <b>your own</b>{" "}
        bank account.
      </>
    );
  }, [selectedFiatCurrency]);

  const navigate = useNavigate();
  const navigateToUSD = () => {
    navigate(ROUTES.USD_ACCOUNT);
  };

  return (
    <DashboardLayout title="Deposits" action={CurrencySelector}>
      <div className="mb-16 bg-white px-4 md:px-8 pt-4 md:pt-8 pb-4 md:pb-12 rounded-2xl border border-gray-100">
        <h3 className="text-gray-500 font-source-serif text-3xl pb-4">
          How it works
        </h3>
        <div className="flex flex-col gap-1">
          <div className="mt-2">1. {depositText}</div>
          <div className="mt-2">
            2. We convert and send it to your chosen destination.
          </div>
        </div>
      </div>

      <div className="mb-4 md:mb-16 bg-white px-4 md:px-8 pt-4 md:pt-8 pb-8 md:pb-16 rounded-2xl border border-gray-100">
        <h3 className="text-gray-500 font-source-serif text-3xl pb-8">
          We will send
        </h3>

        <CurrencySelect
          name="outgoing_currency"
          allowedCurrencies={allowed_outgoing_currencies}
          value={selectedOutCurrency}
          //currencyType={CurrencyType.FIAT}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setSelectedWallet(undefined);
            setSelectedOutCurrency(e.target.value as Currency);
            setOutCurrencyChanged(true);
          }}
        />
        <h3 className="text-gray-500 font-source-serif text-3xl pb-8 pt-8">
          To
        </h3>

        <WalletDropdown
          values={allowedWallets}
          value={selectedWallet}
          onSelect={onSelectWallet}
          //readonly={!!selectedWallet}
          type={
            CURRENCY_MAP[selectedOutCurrency].type === CurrencyType.CRYPTO
              ? "crypto"
              : "fiat"
          }
          onCreate={onCreateWallet}
        />
        <h3 className="text-gray-500 font-source-serif text-3xl pb-8 pt-8">
          When <b>you</b> send <b>your</b> funds to
        </h3>
        {selectedFiatCurrency === Currency.USD &&
        !selectedPaymentDetails?.account_number ? (
          <Button
            className="px-5"
            contentClassName="flex gap-5"
            variant={Button.variant.PRIMARY}
            onClick={navigateToUSD}
          >
            <Icon
              icon="Arrow-right"
              size={24}
              className="text-white"
              disableFill
            />
            Get your USD account details{" "}
          </Button>
        ) : (
          <ArchwayDetails
            show={!!selectedPaymentDetails}
            currency={selectedFiatCurrency}
            message={paymentDetailsMessage}
            data={{
              ...selectedPaymentDetails,
            }}
            removeReference={true}
          />
        )}
        {/* <ArchwayDetails
          show={!!selectedPaymentDetails}
          currency={selectedFiatCurrency}
          message={paymentDetailsMessage}
          data={{
            ...selectedPaymentDetails,
          }}
        /> */}

        <NewWalletModal
          isOpen={showWalletForm}
          close={() => setShowWalletForm(false)}
          callback={() => {
            toastSuccess("Wallet created successfully");
            setShowWalletForm(false);
          }}
        />
        <NewBankAccountModal
          isOpen={showBankAccountForm}
          close={() => setShowBankAccountForm(false)}
          callback={() => {
            toastSuccess("Bank Account created successfully");
            setShowBankAccountForm(false);
          }}
        />

        <div className="flex flex-row justify-between items-center pt-8 border-gray-50 border-b pb-4 mb-4">
          <div className="theme-caption uppercase basis-2/3">
            Instruction for deposits
          </div>

          <div className="theme-caption uppercase basis-1/3">
            Monthly limit left
          </div>
        </div>

        <div className="flex flex-row justify-between items-center gap-4">
          <div className="basis-2/3">
            You can exchange up to &nbsp;
            {formatAmount(monthlyLimit)}
            &nbsp; EUR or equivalent in other currencies within this calendar
            month. Get in touch with &nbsp;
            <a
              href="mailto:payments@archway.finance"
              className="text-skyblue-700"
            >
              payments@archway.finance
            </a>
            &nbsp; to increase your limits.
          </div>

          <div className="basis-1/3">{formatAmount(monthlyLeft)}</div>
        </div>
      </div>
    </DashboardLayout>
  );
};
